import { useEffect, useMemo, useState } from 'react';
import { ShieldCheck, AlertCircle, Check, X, ChevronDown, ChevronRight, Building2 } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { onboardingAdminApi, tenantsApi, ApiError } from '@solara/api-client';
import type { KycDocumentDto, TenantDto } from '@solara/api-client';

// UAE Decision 64/2025 — the typical set of KYC documents tenants must
// submit to become an active ASP customer. Mirrors the backend
// `REQUIRED_DOCUMENTS_BY_LEGAL_FORM.LLC` default; we don't currently expose
// each tenant's specific required list, so this is the assumed baseline.
const DEFAULT_REQUIRED = ['TradeLicense', 'MOA', 'EmiratesId', 'AddressProof', 'BankReference'];

interface GroupedTenant {
  tenantId: string;
  tenant: TenantDto | null;
  submitted: KycDocumentDto[];
  uploadedTypes: Set<string>;
  missingTypes: string[];
}

export default function KycReviewQueuePage() {
  const { t } = useTranslation('p1');
  const [docs, setDocs] = useState<KycDocumentDto[]>([]);
  const [tenants, setTenants] = useState<TenantDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      const [d, ts] = await Promise.all([
        onboardingAdminApi.reviewQueue(),
        tenantsApi.list().catch(() => [] as TenantDto[]),
      ]);
      setDocs(d);
      setTenants(ts);
      // Expand by default if there's only one tenant so the page is
      // immediately actionable on small datasets.
      if (d.length > 0) {
        const tIds = Array.from(new Set(d.map((x) => x.tenantId)));
        if (tIds.length === 1) setExpanded({ [tIds[0]!]: true });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  const groups: GroupedTenant[] = useMemo(() => {
    const tenantById = new Map(tenants.map((tn) => [tn.id, tn]));
    const byTenant = new Map<string, KycDocumentDto[]>();
    for (const d of docs) {
      const arr = byTenant.get(d.tenantId) ?? [];
      arr.push(d);
      byTenant.set(d.tenantId, arr);
    }
    const out: GroupedTenant[] = [];
    for (const [tenantId, submitted] of byTenant.entries()) {
      const uploadedTypes = new Set(submitted.map((s) => s.documentType));
      const missingTypes = DEFAULT_REQUIRED.filter((rt) => !uploadedTypes.has(rt));
      out.push({
        tenantId,
        tenant: tenantById.get(tenantId) ?? null,
        submitted,
        uploadedTypes,
        missingTypes,
      });
    }
    // Sort: tenants with missing docs first (they need ops attention), then
    // by tenant name when available.
    out.sort((a, b) => {
      if (a.missingTypes.length !== b.missingTypes.length)
        return b.missingTypes.length - a.missingTypes.length;
      return (a.tenant?.legalNameEn ?? a.tenantId).localeCompare(
        b.tenant?.legalNameEn ?? b.tenantId,
      );
    });
    return out;
  }, [docs, tenants]);

  async function approve(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await onboardingAdminApi.approveDocument(id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusyId(null);
    }
  }

  async function reject(id: string) {
    const reason = reasons[id];
    if (!reason || reason.length < 5) {
      setError(t('kyc.rejectReason'));
      return;
    }
    setBusyId(id);
    setError(null);
    try {
      await onboardingAdminApi.rejectDocument(id, reason);
      setReasons((r) => ({ ...r, [id]: '' }));
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusyId(null);
    }
  }

  function toggle(tenantId: string) {
    setExpanded((e) => ({ ...e, [tenantId]: !e[tenantId] }));
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('kyc.queueTitle')}</h1>
        <p className="text-fg-muted text-sm">{t('kyc.queueSubtitle')}</p>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Card className="rounded-2xl">
          <CardBody>
            <p className="text-xs text-fg-muted uppercase tracking-wide">
              {t('kyc.tenantsCount', { count: groups.length })}
            </p>
            <p className="text-3xl font-semibold mt-2">{groups.length}</p>
          </CardBody>
        </Card>
        <Card className="rounded-2xl">
          <CardBody>
            <p className="text-xs text-fg-muted uppercase tracking-wide">
              {t('kyc.queueCount', { count: docs.length })}
            </p>
            <p className="text-3xl font-semibold mt-2">{docs.length}</p>
          </CardBody>
        </Card>
        <Card className="rounded-2xl">
          <CardBody>
            <p className="text-xs text-fg-muted uppercase tracking-wide">{t('kyc.missingDocs')}</p>
            <p className="text-3xl font-semibold mt-2 text-warning">
              {groups.reduce((s, g) => s + g.missingTypes.length, 0)}
            </p>
          </CardBody>
        </Card>
      </div>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            {t('kyc.queueTitle')}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {groups.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">{t('kyc.noTenants')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {groups.map((g) => {
                const isOpen = expanded[g.tenantId] === true;
                const uploadedCount = g.uploadedTypes.size;
                const requiredCount = DEFAULT_REQUIRED.length;
                const allUploaded = g.missingTypes.length === 0;
                return (
                  <li key={g.tenantId} className="py-3 text-sm">
                    <button
                      onClick={() => toggle(g.tenantId)}
                      className="w-full text-start flex items-start justify-between gap-3 hover:bg-surface-muted -mx-2 px-2 py-2 rounded"
                    >
                      <div className="flex items-start gap-2 min-w-0">
                        {isOpen ? (
                          <ChevronDown className="h-4 w-4 mt-0.5 text-fg-muted shrink-0" />
                        ) : (
                          <ChevronRight className="h-4 w-4 mt-0.5 text-fg-muted shrink-0" />
                        )}
                        <Building2 className="h-4 w-4 mt-0.5 text-fg-muted shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium truncate">
                            {g.tenant?.legalNameEn ?? `Tenant ${g.tenantId.slice(0, 8)}`}
                          </p>
                          <p className="text-xs text-fg-muted">
                            {g.tenant?.trn && <span className="font-mono">TRN {g.tenant.trn}</span>}
                            {g.tenant?.trn && ' · '}
                            {t('kyc.uploadedOf', { uploaded: uploadedCount, required: requiredCount })}
                          </p>
                          {g.missingTypes.length > 0 && (
                            <p className="text-xs text-warning mt-1">
                              {t('kyc.missingDocs')}{' '}
                              <span className="font-medium">{g.missingTypes.join(', ')}</span>
                            </p>
                          )}
                        </div>
                      </div>
                      <Badge
                        tone={allUploaded ? 'success' : 'warning'}
                        className="text-[10px] shrink-0"
                      >
                        {uploadedCount}/{requiredCount}
                      </Badge>
                    </button>

                    {isOpen && (
                      <ul className="mt-3 ms-6 space-y-3 border-s border-border ps-4">
                        <li className="text-xs uppercase tracking-wide text-fg-muted">
                          {t('kyc.submittedDocs')}
                        </li>
                        {g.submitted.map((d) => (
                          <li key={d.id} className="text-sm space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="font-medium">{d.documentType}</p>
                                <p className="text-xs text-fg-muted">
                                  {t('kyc.submitted')}{' '}
                                  {new Date(d.uploadedAt).toLocaleString()}
                                </p>
                                {d.expiryDate && (
                                  <Badge tone="warning" className="mt-1 text-[10px]">
                                    {t('compliance.expiringSoon', {
                                      days: Math.max(
                                        0,
                                        Math.round(
                                          (new Date(d.expiryDate).getTime() - Date.now()) / 86400000,
                                        ),
                                      ),
                                    })}
                                  </Badge>
                                )}
                              </div>
                              <Button
                                onClick={() => void approve(d.id)}
                                disabled={busyId === d.id}
                              >
                                <Check className="mr-1 h-3 w-3" />
                                {t('actions.approve')}
                              </Button>
                            </div>
                            <div className="flex items-center gap-2">
                              <Input
                                value={reasons[d.id] ?? ''}
                                onChange={(e) =>
                                  setReasons((r) => ({ ...r, [d.id]: e.target.value }))
                                }
                                placeholder={t('kyc.rejectReason')}
                                className="text-sm"
                              />
                              <Button
                                variant="ghost"
                                onClick={() => void reject(d.id)}
                                disabled={busyId === d.id}
                              >
                                <X className="mr-1 h-3 w-3" />
                                {t('actions.reject')}
                              </Button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
